"use client";

import { type AppBrandConfig, DEFAULT_APP_BRAND } from "@/lib/brands";
import { mightSeeFirstRunIntro } from "@/lib/experiments/firstRunIntro";
import { getGuestTotalUsageSeconds } from "@/lib/livekit-agent-usage-storage";
import React, { useEffect, useState } from "react";
import { DecorativeBackground, MouseGradient } from "./fullscreen";

const SkeletonShape = ({ className }: { className: string }) => (
  <div className={`relative overflow-hidden rounded bg-white/10 ${className}`} aria-hidden="true">
    <span
      className="app-skeleton-shimmer pointer-events-none absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-transparent via-white/[0.12] to-transparent opacity-0"
      data-app-loading-shimmer
    />
  </div>
);

const SkeletonLoader = ({
  brand = DEFAULT_APP_BRAND,
  firstRunAware = false,
}: {
  brand?: AppBrandConfig;
  /** Where the first-run intro can follow, leave out the classroom film for possible new guests. */
  firstRunAware?: boolean;
}) => {
  // Decided after mount, in the same pass in which the background learns the
  // viewport, so the film never paints before the decision.
  const [showMedia, setShowMedia] = useState(!firstRunAware);
  useEffect(() => {
    if (firstRunAware) setShowMedia(!mightSeeFirstRunIntro(undefined, getGuestTotalUsageSeconds()));
  }, [firstRunAware]);

  return (
    <div
      className="relative min-h-[100dvh] overflow-hidden bg-[#0F0F14] text-white"
      data-app-loading-shell
      aria-busy="true"
    >
      <DecorativeBackground
        isCanvasVisible={false}
        variant={brand.variant}
        media={brand.backgroundMedia}
        showMedia={showMedia}
      />
      <MouseGradient />

      <div className="relative z-20 flex min-h-[100dvh] flex-col">
        <header className="flex min-h-24 items-start justify-between px-4 pt-10 sm:px-6 md:min-h-28 md:px-10 md:pt-8">
          <div>
            <div className="flex items-center gap-3">
              <SkeletonShape className="h-10 w-10 rounded-full" />
              <SkeletonShape className="hidden h-7 w-32 rounded-lg md:block" />
            </div>
            <SkeletonShape className="mt-4 h-3 w-40 bg-white/[0.06]" />
          </div>
          <div className="flex items-center gap-3">
            <SkeletonShape className="h-9 w-9 rounded-full" />
            <SkeletonShape className="h-9 w-9 rounded-full" />
          </div>
        </header>

        <main
          className="flex min-h-0 flex-1 flex-col items-center justify-center gap-8 px-4 pb-10"
          data-app-loading-speaking-shell
        >
          <SkeletonShape className="h-64 w-64 rounded-full bg-white/[0.06] ring-1 ring-white/[0.04] md:h-96 md:w-96" />

          <div className="flex items-center justify-center gap-6" data-app-loading-controls>
            <SkeletonShape className="h-12 w-12 rounded-full" />
            <SkeletonShape className="h-16 w-16 rounded-full bg-white/[0.12]" />
            <SkeletonShape className="h-12 w-12 rounded-full" />
          </div>
        </main>
      </div>

      <span className="sr-only" role="status">
        Preparing {brand.name}
      </span>

      <style>{`
        @keyframes app-skeleton-shimmer {
          0%, 20% {
            transform: translate3d(-120%, 0, 0);
          }
          70%, 100% {
            transform: translate3d(220%, 0, 0);
          }
        }

        @media (prefers-reduced-motion: no-preference) {
          .app-skeleton-shimmer {
            animation: app-skeleton-shimmer 2.4s cubic-bezier(0.16, 1, 0.3, 1) infinite;
            opacity: 1;
            will-change: transform;
          }
        }
      `}</style>
    </div>
  );
};

export default SkeletonLoader;
